import { DamagesCard } from "@/components/features/matter/settlements/DamagesCard";
import { SettlementsDemandCard } from "@/components/features/matter/settlements/SettlementsDemandCard";
import { TrustLedgerCard } from "@/components/features/matter/settlements/TrustLedgerCard";
import {
  Button,
  Card,
  CardHeader,
  CurrencyWrapper,
  ServerErrorFallbackCard,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { PageHighlightCards } from "@/components/ui/PageHighlightsCards/PageHighlightCards";
import { useUser } from "@/components/UserProvider";
import {
  type SettlementViewModel,
  useSettlementsViewModel,
} from "@/hooks/matter-view-models/use-settlements-view-model";
import { useGlobalModal, useHandleSearchParams } from "@/hooks/useGlobalModal";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Matter,
  type MatterExpense,
  type MatterLien,
  type MedicalTreatmentBill,
} from "@/lib/types";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { calculateLienTotals } from "@/lib/utils/matters/lien-utils";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { calculateNetToClient } from "@/lib/utils/matters/settlement-utils";
import { ErrorBoundary } from "@sentry/react";
import { Handshake } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface MatterSettlementDemandsProps {
  matter: Matter;
  searchSettlementDemandId?: string;
  searchTrustCheckId?: string;
}

type MatterWithSettlementCollections = Partial<Matter> & {
  expenses?: MatterExpense[];
  id?: string;
  liens?: MatterLien[];
  medicalTreatmentBills?: MedicalTreatmentBill[];
};

const EMPTY_EXPENSES: MatterExpense[] = [];
const EMPTY_LIENS: MatterLien[] = [];
const EMPTY_MEDICAL_TREATMENT_BILLS: MedicalTreatmentBill[] = [];

const MatterSettlementDemandsDangerous = ({
  matter,
  searchSettlementDemandId,
  searchTrustCheckId,
}: MatterSettlementDemandsProps) => {
  const { hasPagePermission, hasActionPermission } = useUser();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailSettlements,
  );
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSettlementDemand,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasTrustCheckViewPermission = Boolean(true);
  const hasTrustCheckModuleAccess =
    !matter.incidentType || matter.incidentType.trustCheckModuleAccess;
  const canViewTrustLedger =
    hasTrustCheckViewPermission && Boolean(hasTrustCheckModuleAccess);

  const { openSettlementDemandModal } = useGlobalModal();
  const matterCollections = matter as MatterWithSettlementCollections;
  const matterId = matter.id;
  const expenses = (matterCollections.expenses ??
    matterCollections.matterExpense ??
    EMPTY_EXPENSES) as MatterExpense[];
  const liens = matterCollections.liens ?? EMPTY_LIENS;
  const medicalTreatmentBills =
    matterCollections.medicalTreatmentBills ?? EMPTY_MEDICAL_TREATMENT_BILLS;

  const settlementsViewModel = useSettlementsViewModel(matterId);

  const settlementGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        coverageType?: string;
        demands: SettlementViewModel[];
        earliestDateSent: number;
        insuranceId?: string;
        isMedpay: boolean;
      }
    >();

    settlementsViewModel.settlements.forEach((settlement) => {
      const coverageType = settlement.coverageType ?? undefined;
      const isMedpay =
        coverageType === "MedPay" ||
        (settlement.type === "Offer" && settlement.medPay === true);
      const groupKey = JSON.stringify([
        settlement.insuranceId ?? null,
        isMedpay,
        coverageType ?? null,
      ]);
      const settlementDate =
        settlement.type === "Offer"
          ? settlement.dateReceived
          : settlement.dateSent;
      const parsedDate = settlementDate ? Date.parse(settlementDate) : 0;
      const date = Number.isNaN(parsedDate) ? 0 : parsedDate;
      const existingGroup = groups.get(groupKey);

      if (existingGroup) {
        existingGroup.demands.push(settlement);
        if (
          date > 0 &&
          (existingGroup.earliestDateSent === 0 ||
            date < existingGroup.earliestDateSent)
        ) {
          existingGroup.earliestDateSent = date;
        }
        return;
      }

      groups.set(groupKey, {
        coverageType,
        demands: [settlement],
        earliestDateSent: date,
        insuranceId: settlement.insuranceId,
        isMedpay,
      });
    });

    return Array.from(groups.entries())
      .map(([groupKey, group]) => ({ groupKey, ...group }))
      .sort((a, b) => b.earliestDateSent - a.earliestDateSent);
  }, [settlementsViewModel.settlements]);

  const { totalLiens } = calculateLienTotals(liens);
  const { totalExpenses } = calculateExpenseTotals(expenses);
  const { totalBill } = calculateMedicalTreatmentBillTotals(
    medicalTreatmentBills,
  );

  const { totalAcceptedSettlements, netToFirm } = settlementsViewModel;

  const totalCosts = totalLiens + totalExpenses + totalBill;

  const netToClient = calculateNetToClient(
    totalAcceptedSettlements,
    totalCosts,
    netToFirm,
  );

  const pageHighlightCards = useMemo(() => {
    return [
      {
        title: "Total settlement amount",
        value: totalAcceptedSettlements,
        isCurrency: true,
      },
      {
        title: "Total deductions",
        value: totalCosts,
        isCurrency: true,
        onHoverText: (
          <div className="space-y-1">
            <Text
              variant="subHeading"
              color="primary"
              as="div"
              className="flex gap-1"
            >
              <span>Medicals:</span>
              <CurrencyWrapper value={totalBill} />
            </Text>
            <Text
              variant="subHeading"
              color="primary"
              as="div"
              className="flex gap-1"
            >
              <span>Costs:</span>
              <CurrencyWrapper value={totalExpenses} />
            </Text>
            <Text
              variant="subHeading"
              color="primary"
              as="div"
              className="flex gap-1"
            >
              <span>Liens:</span>
              <CurrencyWrapper value={totalLiens} />
            </Text>
          </div>
        ),
      },
      {
        title: "Net to firm",
        value: netToFirm,
        isCurrency: true,
        showCents: true,
      },
      {
        title: "Net to client",
        value: netToClient,
        isCurrency: true,
      },
    ];
  }, [
    totalAcceptedSettlements,
    totalCosts,
    netToFirm,
    totalLiens,
    totalBill,
    totalExpenses,
    netToClient,
  ]);

  const { handleSettlementDemandSearch, handleTrustCheckSearch } =
    useHandleSearchParams();
  const processedSearches = useRef<Set<string>>(new Set());

  // Handle URL-based vehicle search using the global hook
  useEffect(() => {
    if (!searchSettlementDemandId) return;

    const searchKey = `settlement-demand-${searchSettlementDemandId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleSettlementDemandSearch(matter, searchSettlementDemandId);
  }, [searchSettlementDemandId, matter, handleSettlementDemandSearch]);

  useEffect(() => {
    if (!searchTrustCheckId) return;

    const searchKey = `trust-check-${searchTrustCheckId}`;
    if (processedSearches.current.has(searchKey)) {
      return;
    }
    processedSearches.current.add(searchKey);

    handleTrustCheckSearch(matter, searchTrustCheckId);
  }, [searchTrustCheckId, matter, handleTrustCheckSearch]);

  const [selectedTab, setSelectedTab] = useState<
    "settlements" | "trust-ledger"
  >(() => {
    if (hasViewPermission && !canViewTrustLedger) {
      return "settlements";
    }
    if (!hasViewPermission && canViewTrustLedger) {
      return "trust-ledger";
    }
    return "settlements"; // Default to settlements if both permissions exist
  });

  const renderCreateButtons = () =>
    hasCreatePermission ? (
      <>
        <Button
          onClick={() => {
            openSettlementDemandModal({
              matterId,
              settlementId: undefined,
              negotiationType: "Demand",
              editing: false,
              initialTab: "details",
              selectedInsurance: undefined,
            });
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Handshake className="h-4 w-4" />
          Create new demand
        </Button>
        <Button
          onClick={() => {
            openSettlementDemandModal({
              matterId,
              settlementId: undefined,
              negotiationType: "Offer",
              editing: false,
              initialTab: "details",
              selectedInsurance: undefined,
            });
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Handshake className="h-4 w-4" />
          Create new offer
        </Button>
      </>
    ) : null;

  return (
    <div className="flex flex-col gap-y-4 p-6">
      <Text as="h1" variant="title">
        Settlements
      </Text>
      {!hasViewPermission && (
        <Card className="w-full border-2">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view settlements.
            </Text>
          </CardHeader>
        </Card>
      )}
      {hasViewPermission && (
        <PageHighlightCards items={pageHighlightCards} variant="summary" />
      )}

      {hasViewPermission && canViewTrustLedger && (
        <div className="flex w-full flex-1 grow flex-wrap items-center gap-x-2">
          <Tabs
            value={selectedTab}
            onValueChange={(value) => {
              setSelectedTab(value as "settlements" | "trust-ledger");
            }}
          >
            <TabsList>
              <TabsTrigger value="settlements">
                <Text as="span" variant="body" className="text-current">
                  Settlements
                </Text>
              </TabsTrigger>
              <TabsTrigger value="trust-ledger">
                <Text as="span" variant="body" className="text-current">
                  Trust ledger
                </Text>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex grow" />
          <div className="flex gap-x-2">
            {selectedTab === "settlements" && renderCreateButtons()}
          </div>
        </div>
      )}

      {/* Show create buttons without tabs when only settlements are viewable */}
      {hasViewPermission && !canViewTrustLedger && (
        <div className="flex w-full flex-1 grow flex-wrap items-center justify-end gap-x-2">
          <div className="flex gap-x-2">{renderCreateButtons()}</div>
        </div>
      )}

      {hasViewPermission && (
        <>
          {selectedTab === "settlements" ? (
            <Card className="flex w-full flex-col gap-4 border p-4">
              <div className="flex flex-col gap-y-1">
                <Text
                  variant="body"
                  color="primary"
                  className="text-lg font-semibold"
                >
                  Settlements
                </Text>
                <Text variant="subHeading">
                  Settlement tracking and trust account deposits
                </Text>
              </div>
              {settlementsViewModel.settlements.length === 0 ? (
                <Card className="w-full border-2 border-dashed">
                  <CardHeader className="space-y-1 text-center">
                    <Text
                      variant="body"
                      className="text-muted-foreground font-semibold"
                    >
                      You haven&apos;t added a settlement demand or offer yet.
                    </Text>
                    <Text variant="subHeading">
                      Create your first by clicking &apos;Create new
                      demand&apos; or &apos;Create new offer&apos;
                    </Text>
                  </CardHeader>
                </Card>
              ) : (
                <>
                  {settlementGroups.map((group) => {
                    const insurance = matter.insurances.find(
                      (i) => i.id === group.insuranceId,
                    );
                    return (
                      <SettlementsDemandCard
                        key={group.groupKey}
                        demands={group.demands}
                        isMedpay={group.isMedpay}
                        coverageType={group.coverageType}
                        insurance={insurance}
                        matter={matter}
                      />
                    );
                  })}
                </>
              )}
            </Card>
          ) : (
            <TrustLedgerCard matter={matter} />
          )}
        </>
      )}

      {hasViewPermission && selectedTab === "settlements" && (
        <DamagesCard matter={matter} />
      )}
    </div>
  );
};

export const MatterSettlementDemands = (
  props: MatterSettlementDemandsProps,
) => {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-6">
          <ServerErrorFallbackCard />
        </div>
      }
    >
      <MatterSettlementDemandsDangerous {...props} />
    </ErrorBoundary>
  );
};
